import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';

import {
  AnalysisProseCard,
  AnalysisSectionLayout,
  PROSE_CARD_STAGGER_SLOTS,
  SectionBlocks,
  type SectionBlock,
} from '../../components';
import {
  useAnalysisFromContext,
  type AnalysisSectionProps,
} from '../../utils/useAnalysisFromContext';
import { hasContent } from '../../utils/hasContent';
import type { CultureContextData } from './CultureContext.types';
import {
  CulturalMoments,
  CulturalSignals,
  GenerationalPerspectives,
  TimelyOpportunities,
  withTimelyContent,
} from './components';
import { useTranslation } from 'react-i18next';

// The Cultural White Space card leads the cascade from slot 0, then an empty slot
// lets it settle before each section swipes up in order.
const CARD_SETTLE_GAP = 1;
const sectionStagger = (order: number) => PROSE_CARD_STAGGER_SLOTS + CARD_SETTLE_GAP + 1 + order;

export const CultureContext = ({
  analysis: analysisProp,
  exporting,
}: AnalysisSectionProps = {}) => {
  const { t } = useTranslation('analysis');
  const title = t('analysisResult.cultureContext.cultureContext');
  const analysis = useAnalysisFromContext(analysisProp);
  const cultureContext = (analysis.structuredData?.cultureContext ?? {}) as CultureContextData;

  const signals = cultureContext.culturalSignals;
  const generations = cultureContext.generationalInsights;
  const relevantMoments = cultureContext.relevantMoments ?? [];
  const culturalTrends = cultureContext.culturalTrends ?? [];

  const blocks: SectionBlock[] = [
    {
      key: 'white-space',
      hasData: hasContent(cultureContext.culturalWhiteSpace),
      node: (
        <AnalysisProseCard
          title={t('analysisResult.culturalWhiteSpace.culturalWhiteSpace')}
          description={cultureContext.culturalWhiteSpace ?? ''}
          descriptionVariant="statement"
          columns={1}
          staggerStart={0}
        />
      ),
    },
    {
      key: 'signals',
      hasData: hasContent([signals?.emerging, signals?.mainstream, signals?.declining]),
      node: (
        <StaggerItem index={sectionStagger(0)} variant="swipeUp">
          <CulturalSignals culturalSignals={signals} />
        </StaggerItem>
      ),
    },
    {
      key: 'generational',
      hasData: hasContent([
        generations?.genZ,
        generations?.millennial,
        generations?.genX,
        generations?.boomer,
        cultureContext.memeMoments,
        cultureContext.socialMovements,
      ]),
      node: (
        <StaggerItem index={sectionStagger(1)} variant="swipeUp">
          <GenerationalPerspectives
            generationalInsights={generations}
            memeMoments={cultureContext.memeMoments}
            socialMovements={cultureContext.socialMovements}
          />
        </StaggerItem>
      ),
    },
    {
      key: 'opportunities',
      hasData: withTimelyContent(cultureContext.timelyOpportunities).length > 0,
      node: (
        <StaggerItem index={sectionStagger(2)} variant="swipeUp">
          <TimelyOpportunities timelyOpportunities={cultureContext.timelyOpportunities} />
        </StaggerItem>
      ),
    },
    {
      key: 'moments',
      hasData: hasContent(relevantMoments),
      node: (
        <StaggerItem index={sectionStagger(3)} variant="swipeUp">
          <CulturalMoments
            title={t('analysisResult.cultureContext.culturalMoments')}
            subtitle={t('analysisResult.cultureContext.keyMomentsShapingCulturalConversations')}
            moments={relevantMoments}
          />
        </StaggerItem>
      ),
    },
    {
      key: 'trends',
      hasData: hasContent(culturalTrends),
      node: (
        <StaggerItem index={sectionStagger(4)} variant="swipeUp">
          <CulturalMoments
            title={t('analysisResult.cultureContext.culturalTrends')}
            subtitle={t('analysisResult.cultureContext.emergingPatternsInCulturalBehavior')}
            moments={culturalTrends}
          />
        </StaggerItem>
      ),
    },
  ];

  return (
    <AnalysisSectionLayout
      analysisId={analysis.id}
      brand={analysis.brandName}
      section={ROUTE_PATTERNS.IQ_SECTION_CULTURE_CONTEXT}
      title={title}
      subtitle={t('analysisResult.cultureContext.culturalLandscapeZeitgeist', {
        brandName: analysis.brandName,
      })}
      exporting={exporting}
    >
      <SectionBlocks category={title} blocks={blocks} />
    </AnalysisSectionLayout>
  );
};
